import { hexToRgb } from '@OnArk/utils/toColor';

/**
 * Church theming for bridge components (CSS Modules + CSS variables, no
 * Emotion, no Tailwind — see docs/v2-guest-builder-incremental-migration.md).
 * Moved here from Sections/Video/accent.ts unchanged (still the video
 * player's accent type) so every bridge component shares one source for
 * "how does a church's color reach a CSS variable".
 */

export interface ChurchAccent {
  r: number;
  g: number;
  b: number;
}

/**
 * Church colors reach the client in several shapes ({r,g,b,a} JSON, hex
 * string, rgb() string, or a JSON-encoded string — see _app's theme
 * normalization). Fold them all to channels; null = keep the CSS default.
 */
export function normalizeAccent(input: unknown): ChurchAccent | null {
  if (!input) return null;
  if (typeof input === 'string') {
    try {
      if (input.startsWith('#')) return normalizeAccent(hexToRgb(input));
      if (input.startsWith('rgb')) {
        const [r, g, b] = input.replace(/rgba?\((.+)\)/, '$1').split(',');

        return { b: parseInt(b), g: parseInt(g), r: parseInt(r) };
      }

      return normalizeAccent(JSON.parse(input));
    } catch {
      return null;
    }
  }
  const c = input as Partial<ChurchAccent>;

  return typeof c.r === 'number' &&
    typeof c.g === 'number' &&
    typeof c.b === 'number'
    ? { b: c.b, g: c.g, r: c.r }
    : null;
}

/** buttonStyle → the church's chosen corner radius, as a bridge-component CSS var. */
const BUTTON_RADIUS: Record<string, string> = {
  PILL: '20px',
  ROUND: '6px',
  SQUARE: '0',
};

/** theme.ts `theme.church` defaults, as raw RGB channel strings. */
const DEFAULT_ACCENT = '123 44 255';
const DEFAULT_ACCENT_TEXT = '255 255 255';
const DEFAULT_RADIUS = BUTTON_RADIUS.ROUND;

export interface ChurchThemeInput {
  /** church.buttonColor — any shape normalizeAccent handles. */
  buttonColor?: unknown;
  /** church.buttonTextColor — any shape normalizeAccent handles. */
  buttonTextColor?: unknown;
  buttonStyle?: string | null;
}

/**
 * CSS custom properties a bridge component reads. Raw RGB channels, like
 * `--church-accent` in app/globals.css, so `rgb(var(--church-accent) / .1)`
 * alpha composition works on both routers. Missing or unparseable colors
 * fall back to theme.ts's `theme.church` defaults, never to `undefined`
 * (an absent CSS var would just fail silently at paint time).
 */
export function churchCssVars(
  church: ChurchThemeInput | null | undefined
): Record<`--${string}`, string> {
  const accent = normalizeAccent(church?.buttonColor);
  const accentText = normalizeAccent(church?.buttonTextColor);
  const radius =
    (church?.buttonStyle && BUTTON_RADIUS[church.buttonStyle]) ||
    DEFAULT_RADIUS;

  return {
    '--church-accent': accent
      ? `${accent.r} ${accent.g} ${accent.b}`
      : DEFAULT_ACCENT,
    '--church-accent-text': accentText
      ? `${accentText.r} ${accentText.g} ${accentText.b}`
      : DEFAULT_ACCENT_TEXT,
    '--church-button-radius': radius,
  };
}
